// The specialist units: SpInfantryGob (the takeover engineer — "firing" captures the
// structure and consumes the specialist), ArtilleryGob (+ ArtilleryShotGob at kwcTile/2),
// AndyGob (the hero: self-heal loop healing armor/60 each second via a delayed kmidHeal
// chain; + AndyShotGob at kwcTile) and FoxGob (Andy's twin with her own consts).
// game/SpInfantry.cpp + game/Artillery.cpp + game/Andy.cpp (all of each).

import type { SimContext } from '../context.ts';
import {
  Gob,
  kfGobActive,
  kfGobStructure,
  kfGobLayerDepthSorted,
  kfGobLayerSurfaceDecal,
} from './gob.ts';
import { MobileUnitGob } from './mobileunit.ts';
import { UnitGob } from './unitgob.ts';
import { StructGob } from './struct.ts';
import {
  MobileUnitConsts,
  SpConsts,
  InitMobileUnitConstsClass,
  kfUntcNotifyEnemyNearby,
  kfUntcLargeDefog,
  kwcTile,
  kwcTile16th,
} from './unitconsts.ts';
import {
  kgtTakeoverSpecialist,
  kgtArtillery,
  kgtArtilleryShot,
  kgtAndy,
  kgtFox,
  kgtAndyShot,
} from './gobtypes.ts';
import {
  kutTakeoverSpecialist,
  kutArtillery,
  kutAndy,
  kutFox,
  kupgmAdvancedHRC,
  kupgmAdvancedVTS,
} from '../player/side.ts';
import { kumResearchCenter } from '../player/masks.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  knHandled,
  knDeleted,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedUpdate,
  kmidDelete,
  kmidHeal,
  kstDying,
} from '../sm/message.ts';
import { kfAniResetWhenDone, kfAniIgnoreFirstAdvance } from '../anim/animation.ts';
import { WcFromPc32 } from '../math/coords.ts';
import { addfx, divfx, itofx } from '../math/fix.ts';
import { idiv } from '../math/int32.ts';
import { TankShotGob } from './shots.ts';
import { knLimitSupport } from './gobmgr.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxTakeoverSpecialistStructureCaptured,
  ksfxArtilleryFire,
  ksfxArtilleryImpact,
  ksfxAndyFire,
  ksfxLightTankFire,
  ksfxcInfantryDestroyed,
  ksfxcVehicleDestroyed,
  ksfxcMajor01Select,
  ksfxcMajor01Move,
  ksfxcMajor01Attack,
  ksfxcMale03Select,
  ksfxcMale03Move,
  ksfxcMale03Attack,
  ksfxcAndyDestroyed,
  ksfxcAndySelect,
  ksfxcAndyMove,
  ksfxcAndyAttack,
  ksfxcFoxDestroyed,
} from '../data/soundeffects.ts';

//===========================================================================
// SpInfantryGob — game/SpInfantry.cpp

const s_anSpIdleStripIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const s_anSpMovingStripIndices = [17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];

export function InitSpInfantryClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new SpConsts().bind(ctx);
  c.gt = kgtTakeoverSpecialist;
  c.ut = kutTakeoverSpecialist;
  c.umPrerequisites = kumResearchCenter;
  c.upgmPrerequisites = kupgmAdvancedHRC;
  c.anFiringStripIndices = s_anSpIdleStripIndices;
  c.anMovingStripIndices = s_anSpMovingStripIndices;
  c.anIdleStripIndices = s_anSpIdleStripIndices;
  // Sound effects (SpInfantry.cpp:32-39)
  c.sfxImpact = ksfxNothing;
  c.sfxFire = ksfxNothing;
  c.sfxStructureCaptured = ksfxTakeoverSpecialistStructureCaptured;
  c.sfxcDestroyed = ksfxcInfantryDestroyed;
  c.sfxcSelect = ksfxcMajor01Select;
  c.sfxcMove = ksfxcMajor01Move;
  c.sfxcAttack = ksfxcMajor01Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class SpInfantryGob extends MobileUnitGob {
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutTakeoverSpecialist] as MobileUnitConsts);
  }

  // SpInfantry.cpp — only active enemy takeoverable structures are valid targets
  override IsValidTarget(pgobTarget: Gob): boolean {
    if (
      (pgobTarget.GetFlags() & (kfGobStructure | kfGobActive)) !== (kfGobStructure | kfGobActive) ||
      this.IsAlly(pgobTarget.GetSide())
    ) {
      return false;
    }
    return (pgobTarget as StructGob).IsTakeoverable(this.GetOwner() as Player);
  }

  // SpInfantry.cpp — "firing" performs the takeover and consumes the specialist.
  // Returns false ALWAYS (the C++ falls into Deactivate + posted kmidDelete).
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    if (!this.IsValidTarget(puntTarget!)) return false;
    if (!super.Fire(puntTarget, wx, wy, wdx, wdy)) return false;

    (puntTarget as unknown as StructGob).Takeover(this.m_pplr as Player);
    if (this.m_pplr === this.ctx.plrLocal) {
      this.ctx.host.PlaySfx((this.m_puntc as SpConsts).sfxStructureCaptured);
    }
    this.Deactivate();
    this.ctx.smm!.PostMsgIds(kmidDelete, this.m_gid, this.m_gid);
    return false;
  }

  override Idle(): void {
    infantryIdle(this);
  }

  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidDelete) {
      this.Delete();
      return knDeleted;
    }
    if (st === kstDying) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          dieInPlace(this);
          return knHandled;
        case kmidReservedUpdate:
          this.AdvanceAnimation(this.m_ani);
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }
    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

//===========================================================================
// ArtilleryGob + ArtilleryShotGob — game/Artillery.cpp

const s_anArtFiringStripIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const s_anArtMovingStripIndices = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];

export function InitArtilleryClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtArtillery;
  c.ut = kutArtillery;
  c.upgmPrerequisites = kupgmAdvancedVTS;
  c.wf |= kfUntcNotifyEnemyNearby | kfUntcLargeDefog;
  c.anFiringStripIndices = s_anArtFiringStripIndices;
  c.anMovingStripIndices = s_anArtMovingStripIndices;
  c.anIdleStripIndices = s_anArtMovingStripIndices;
  // Sound effects (Artillery.cpp:34-39)
  c.sfxFire = ksfxArtilleryFire;
  c.sfxImpact = ksfxArtilleryImpact;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMajor01Select;
  c.sfxcMove = ksfxcMajor01Move;
  c.sfxcAttack = ksfxcMajor01Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class ArtilleryGob extends MobileUnitGob {
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutArtillery] as MobileUnitConsts);
  }

  // Artillery.cpp — full-damage artillery shot from the frame-1 firing point
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    if (!super.Fire(puntTarget, wx, wy, wdx, wdy)) return false;

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_ani.GetSpecialPoint32(1);

    CreateArtilleryShotGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      this.GetDamageTo(puntTarget!),
      this.m_gid,
      puntTarget!.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);
    return true;
  }
}

export class ArtilleryShotGob extends TankShotGob {
  override GetType(): number {
    return kgtArtilleryShot;
  }
  protected override aniName(): string {
    return 'artilleryshot.anir';
  } // issue #98
}

// Artillery.cpp CreateArtilleryShotGob — kwcTile/2 rate, artilleryshot.anir
export function CreateArtilleryShotGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wxTarget: number,
  wyTarget: number,
  nDamage: number,
  gidOwner: number,
  gidTarget: number,
): ArtilleryShotGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new ArtilleryShotGob(ctx);
  if (
    !pgob.InitShot(
      ctx.animDataFor('artilleryshot.anir'),
      wx,
      wy,
      wxTarget,
      wyTarget,
      nDamage,
      gidOwner,
      gidTarget,
      idiv(kwcTile, 2),
    )
  )
    return null;

  pgob.Launch();
  return pgob;
}

//===========================================================================
// AndyGob + FoxGob + AndyShotGob — game/Andy.cpp

const s_anAndyFiringStripIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const s_anAndyMovingStripIndices = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];
const s_anAndyIdleStripIndices = [17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];

export function InitAndyClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtAndy;
  c.ut = kutAndy;
  c.wf |= kfUntcNotifyEnemyNearby | kfUntcLargeDefog;
  c.anFiringStripIndices = s_anAndyFiringStripIndices;
  c.anMovingStripIndices = s_anAndyMovingStripIndices;
  c.anIdleStripIndices = s_anAndyIdleStripIndices;
  // Sound effects (Andy.cpp:32-37)
  c.sfxFire = ksfxAndyFire;
  c.sfxImpact = ksfxNothing;
  c.sfxcDestroyed = ksfxcAndyDestroyed;
  c.sfxcSelect = ksfxcAndySelect;
  c.sfxcMove = ksfxcAndyMove;
  c.sfxcAttack = ksfxcAndyAttack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class AndyGob extends MobileUnitGob {
  constructor(ctx: SimContext, pmuntc?: MobileUnitConsts) {
    super(ctx, pmuntc ?? (ctx.unitConsts[kutAndy] as MobileUnitConsts));
  }

  // Andy.cpp — the self-heal chain starts 100 ticks after spawn
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    const f = super.Init(wx, wy, pplr, fxHealth, ff);
    this.ctx.smm!.SendDelayedMsgIds(kmidHeal, 100, this.m_gid, this.m_gid);
    return f;
  }

  // Andy.cpp — full-damage andy shot from the frame-1 firing point
  override Fire(
    puntTarget: UnitGob | null,
    wx: number,
    wy: number,
    wdx: number,
    wdy: number,
  ): boolean {
    if (!super.Fire(puntTarget, wx, wy, wdx, wdy)) return false;

    const wdxRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const wdyRnd = ((this.ctx.rng.GetRandom() & 7) - 3) * kwcTile16th;
    const ptSpecial32 = this.m_ani.GetSpecialPoint32(1);

    CreateAndyShotGob(
      this.ctx,
      this.m_wx + WcFromPc32(ptSpecial32.x),
      this.m_wy + WcFromPc32(ptSpecial32.y),
      wx + wdxRnd,
      wy + wdyRnd,
      this.GetDamageTo(puntTarget!),
      this.m_gid,
      puntTarget!.GetId(),
    );

    this.ctx.host.PlaySfx(this.m_pmuntc.sfxFire);
    return true;
  }

  override Idle(): void {
    infantryIdle(this);
  }

  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidHeal) {
      // Re-arm while active; heal armor/60 per second toward full
      if (this.m_ff & kfGobActive) {
        this.ctx.smm!.SendDelayedMsgIds(kmidHeal, 100, this.m_gid, this.m_gid);
      }
      const fxArmorStrength = this.m_pmuntc.GetArmorStrength();
      if (this.m_fxHealth < fxArmorStrength) {
        let fxHealth = addfx(this.m_fxHealth, divfx(fxArmorStrength, itofx(60)));
        if (fxHealth > fxArmorStrength) fxHealth = fxArmorStrength;
        this.SetHealth(fxHealth);
      }
      return knHandled;
    }
    if (st === kstDying) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          dieInPlace(this);
          return knHandled;
        case kmidReservedUpdate:
          this.AdvanceAnimation(this.m_ani);
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }
    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

export function InitFoxClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MobileUnitConsts().bind(ctx);
  c.gt = kgtFox;
  c.ut = kutFox;
  c.wf |= kfUntcNotifyEnemyNearby | kfUntcLargeDefog;
  c.anFiringStripIndices = s_anAndyFiringStripIndices;
  c.anMovingStripIndices = s_anAndyMovingStripIndices;
  c.anIdleStripIndices = s_anAndyIdleStripIndices;
  // Sound effects (Andy.cpp:209-215 ("he never shoots" but fires LightTank))
  c.sfxFire = ksfxLightTankFire;
  c.sfxImpact = ksfxNothing;
  c.sfxcDestroyed = ksfxcFoxDestroyed;
  c.sfxcSelect = ksfxcMale03Select;
  c.sfxcMove = ksfxcMale03Move;
  c.sfxcAttack = ksfxcMale03Attack;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class FoxGob extends AndyGob {
  constructor(ctx: SimContext) {
    // Andy.cpp FoxGob: an AndyGob with Fox's consts
    super(ctx, ctx.unitConsts[kutFox] as MobileUnitConsts);
  }
}

export class AndyShotGob extends TankShotGob {
  override GetType(): number {
    return kgtAndyShot;
  }
  protected override aniName(): string {
    return 'andyshot.anir';
  } // issue #98
}

// Andy.cpp CreateAndyShotGob — kwcTile rate, andyshot.anir (fEagle vestigial)
export function CreateAndyShotGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wxTarget: number,
  wyTarget: number,
  nDamage: number,
  gidOwner: number,
  gidTarget: number,
  _fEagle: boolean = false,
): AndyShotGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new AndyShotGob(ctx);
  if (
    !pgob.InitShot(
      ctx.animDataFor('andyshot.anir'),
      wx,
      wy,
      wxTarget,
      wyTarget,
      nDamage,
      gidOwner,
      gidTarget,
      kwcTile,
    )
  )
    return null;

  pgob.Launch();
  return pgob;
}

//===========================================================================
// shared infantry helpers (identical C++ bodies across the specialist types)

function infantryIdle(self: MobileUnitGob): void {
  switch (self.ctx.rng.GetRandom() & 3) {
    case 0:
      self.m_dir--;
      if (self.m_dir < 0) self.m_dir = 15;
      self.StartAnimation(
        self.m_ani,
        self.m_pmuntc.anIdleStripIndices[self.m_dir],
        0,
        kfAniResetWhenDone,
      );
      break;
    case 1:
      self.m_dir++;
      if (self.m_dir > 15) self.m_dir = 0;
      self.StartAnimation(
        self.m_ani,
        self.m_pmuntc.anIdleStripIndices[self.m_dir],
        0,
        kfAniResetWhenDone,
      );
      break;
    default:
      self.StartAnimation(
        self.m_ani,
        self.m_pmuntc.anIdleStripIndices[self.m_dir],
        0,
        kfAniResetWhenDone,
      );
      break;
  }
}

function dieInPlace(self: MobileUnitGob): void {
  // Andy.cpp:138-146 — Deactivate, then redraw this part of the minimap (it
  // will skip inactive munts)
  const trc = { left: 0, top: 0, right: 0, bottom: 0 };
  self.GetTileRect(trc);

  self.Deactivate();

  self.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);

  self.m_ff = (self.m_ff ^ (kfGobLayerDepthSorted | kfGobLayerSurfaceDecal)) >>> 0;
  self.ctx.host.PlaySfx(`sfxc:${self.m_pmuntc.sfxcDestroyed}`);
  self.m_ani.StartByName('die 3', kfAniIgnoreFirstAdvance);
  self.MarkRedraw();
  self.ctx.smm!.SendDelayedMsgIds(kmidDelete, 1000, self.m_gid, self.m_gid);
}

// self-register (see factory.ts)
registerGobType(kgtTakeoverSpecialist, (ctx) => new SpInfantryGob(ctx));
registerGobType(kgtArtillery, (ctx) => new ArtilleryGob(ctx));
registerGobType(kgtAndy, (ctx) => new AndyGob(ctx));
registerGobType(kgtFox, (ctx) => new FoxGob(ctx));
// issue #98: the shot subclasses are serialized in the TS COMPLETE snapshot (in-flight resume).
registerGobType(kgtArtilleryShot, (ctx) => new ArtilleryShotGob(ctx));
registerGobType(kgtAndyShot, (ctx) => new AndyShotGob(ctx));
